import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import CountUp from "react-countup";
import {
  FiBriefcase,
  FiTrendingUp,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
  FiUserX,
  FiClock,
  FiRefreshCw,
  FiX,
} from "react-icons/fi";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { API_BASE_URL } from "../utils/config";
import "./Dashboard.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const COLORS = [
  "#4F46E5",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
];

const SCORE_COLORS = {
  "0-40": "#EF4444",
  "40-50": "#F97316",
  "50-60": "#F59E0B",
  "60-70": "#EAB308",
  "70-80": "#84CC16",
  "80-90": "#10B981",
  "90-100": "#4F46E5",
};

// Custom Tooltip Components
const CustomStatusTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="px-3.5 py-2.5 bg-slate-900/90 text-white rounded-xl shadow-xl backdrop-blur-md border border-slate-700 text-xs">
        <p className="font-bold mb-1 flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full inline-block"
            style={{ backgroundColor: payload[0].color || "#4f46e5" }}
          />
          {data.name}
        </p>
        <p className="text-slate-300 font-medium">
          Applicants: <strong className="text-white">{data.value}</strong>
        </p>
      </div>
    );
  }
  return null;
};

const CustomScoreTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="px-3.5 py-2.5 bg-slate-900/90 text-white rounded-xl shadow-xl backdrop-blur-md border border-slate-700 text-xs">
        <p className="font-bold mb-1 text-indigo-300">Score Range: {data.range}%</p>
        <p className="text-slate-300 font-medium">
          Candidates: <strong className="text-white">{data.count}</strong>
        </p>
      </div>
    );
  }
  return null;
};

const CustomTrendTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="px-4 py-3 bg-slate-900/95 text-white rounded-xl shadow-2xl backdrop-blur-md border border-slate-700 text-xs">
        <p className="font-bold text-indigo-300 mb-1">{label}</p>
        <p className="text-slate-300 font-medium flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping inline-block" />
          Applications:{" "}
          <strong className="text-emerald-400 text-sm">
            {payload[0].value}
          </strong>
        </p>
      </div>
    );
  }
  return null;
};


const RecruitmentDashboard = () => {
  const [stats, setStats] = useState({
    totalApplicants: 0,
    selected: 0,
    rejected: 0,
    interview: 0,
    totalVacancies: 0,
    statusBreakdown: [],
    monthlyTrend: [],
    scoreDistribution: [],
    qualityMetrics: {
      score50plus: 0,
      score60plus: 0,
      score70plus: 0,
      score80plus: 0,
      score90plus: 0,
    },
    availableRoles: [],
    tatDistribution: [],
  });

  const [allApplications, setAllApplications] = useState([]);
  const [statusRole, setStatusRole] = useState("All");
  const [scoreRole, setScoreRole] = useState("All");
  const [tatRole, setTatRole] = useState("All");
  const [trendRole, setTrendRole] = useState("All");
  const [trendMonth, setTrendMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const getClientId = () => {
    return (
      localStorage.getItem("clientId") ||
      localStorage.getItem("clientCustomId") ||
      ""
    );
  };

  const fetchDashboardData = useCallback(async () => {
    const clientId = getClientId();
    if (!clientId) {
      setError("Please login to access the recruitment dashboard.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 1. Fetch raw applications for reliable calculations & trend fallback
      const [statsRes, appsRes, jobsRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/applications/stats/${clientId}`, {
          params: {
            statusRole,
            scoreRole,
            tatRole,
            trendRole,
            ...(trendMonth ? { trendMonth } : {}),
          },
        }),
        axios.get(`${API_BASE_URL}/applications/all/${clientId}`),
        axios.get(`${API_BASE_URL}/jobs/all/${clientId}`),
      ]);

      let applicationsList = [];
      if (appsRes.status === "fulfilled" && appsRes.value.data.success) {
        applicationsList = appsRes.value.data.applications || [];
        setAllApplications(applicationsList);
      }

      let totalVacanciesCount = 0;
      if (jobsRes.status === "fulfilled" && jobsRes.value.data.success) {
        const jobs = jobsRes.value.data.jobPosts || jobsRes.value.data.data || [];
        totalVacanciesCount = jobs.reduce(
          (acc, j) => acc + (Number(j.openings || j.vacancies) || 1),
          0
        );
      }

      // Extract unique roles from applications & jobs
      const rolesSet = new Set();
      applicationsList.forEach((a) => {
        const r = a.jobId?.role || a.role;
        if (r) rolesSet.add(r);
      });

      let baseStats = {
        totalApplicants: applicationsList.length,
        selected: applicationsList.filter((a) => a.status === "Selected").length,
        rejected: applicationsList.filter((a) => a.status === "Rejected").length,
        interview: applicationsList.filter(
          (a) =>
            a.interviewStatus === "Invited" ||
            a.interviewStatus === "Scheduled" ||
            a.status === "Interview"
        ).length,
        totalVacancies: totalVacanciesCount,
        statusBreakdown: [],
        monthlyTrend: [],
        scoreDistribution: [],
        qualityMetrics: {
          score50plus: 0,
          score60plus: 0,
          score70plus: 0,
          score80plus: 0,
          score90plus: 0,
        },
        availableRoles: Array.from(rolesSet),
        tatDistribution: [],
      };

      if (statsRes.status === "fulfilled" && statsRes.value.data.success) {
        const serverStats = statsRes.value.data.stats || {};
        baseStats = {
          ...baseStats,
          ...serverStats,
          totalVacancies: serverStats.totalVacancies || totalVacanciesCount,
        };
        if (serverStats.availableRoles?.length > 0) {
          baseStats.availableRoles = Array.from(
            new Set([...baseStats.availableRoles, ...serverStats.availableRoles])
          );
        }
      }

      // If quality metrics are missing, compute from raw applications
      if (
        !baseStats.qualityMetrics ||
        (baseStats.qualityMetrics.score50plus === 0 &&
          applicationsList.length > 0)
      ) {
        baseStats.qualityMetrics = {
          score50plus: applicationsList.filter(
            (a) => (a.technicalScore || 0) >= 50
          ).length,
          score60plus: applicationsList.filter(
            (a) => (a.technicalScore || 0) >= 60
          ).length,
          score70plus: applicationsList.filter(
            (a) => (a.technicalScore || 0) >= 70
          ).length,
          score80plus: applicationsList.filter(
            (a) => (a.technicalScore || 0) >= 80
          ).length,
          score90plus: applicationsList.filter(
            (a) => (a.technicalScore || 0) >= 90
          ).length,
        };
      }

      // If status breakdown is missing, compute from applications
      if (
        (!baseStats.statusBreakdown || baseStats.statusBreakdown.length === 0) &&
        applicationsList.length > 0
      ) {
        const statusMap = {};
        applicationsList.forEach((a) => {
          const st = a.status || "Applied";
          statusMap[st] = (statusMap[st] || 0) + 1;
        });
        baseStats.statusBreakdown = Object.entries(statusMap).map(
          ([name, value]) => ({ name, value })
        );
      }

      // If score distribution is missing, compute from applications
      if (
        (!baseStats.scoreDistribution ||
          baseStats.scoreDistribution.length === 0) &&
        applicationsList.length > 0
      ) {
        const ranges = {
          "0-40": 0,
          "40-50": 0,
          "50-60": 0,
          "60-70": 0,
          "70-80": 0,
          "80-90": 0,
          "90-100": 0,
        };
        applicationsList.forEach((a) => {
          const score = Number(a.technicalScore) || 0;
          if (score < 40) ranges["0-40"]++;
          else if (score < 50) ranges["40-50"]++;
          else if (score < 60) ranges["50-60"]++;
          else if (score < 70) ranges["60-70"]++;
          else if (score < 70) ranges["60-70"]++;
          else if (score < 80) ranges["70-80"]++;
          else if (score < 90) ranges["80-90"]++;
          else ranges["90-100"]++;
        });
        baseStats.scoreDistribution = Object.entries(ranges).map(
          ([range, count]) => ({ range, count })
        );
      }

      setStats(baseStats);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError("Failed to load recruitment dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [statusRole, scoreRole, tatRole, trendRole, trendMonth]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Robust Monthly Trend computation
  const computedMonthlyTrend = useMemo(() => {
    if (stats.monthlyTrend && stats.monthlyTrend.length > 0 && !trendMonth) {
      return stats.monthlyTrend;
    }

    const filtered = allApplications.filter((app) => {
      if (trendRole && trendRole !== "All") {
        const appRole = app.jobId?.role || app.role || "";
        if (appRole.toLowerCase() !== trendRole.toLowerCase()) return false;
      }
      return true;
    });

    if (trendMonth) {
      // Daily view for chosen month (YYYY-MM)
      const [yearStr, monthStr] = trendMonth.split("-");
      const targetYear = parseInt(yearStr, 10);
      const targetMonth = parseInt(monthStr, 10);

      const daysInMonth = new Date(targetYear, targetMonth, 0).getDate();
      const dailyMap = {};
      for (let d = 1; d <= daysInMonth; d++) {
        dailyMap[d] = 0;
      }

      filtered.forEach((app) => {
        const dateVal = app.appliedAt || app.createdAt;
        if (!dateVal) return;
        const d = new Date(dateVal);
        if (
          d.getFullYear() === targetYear &&
          d.getMonth() + 1 === targetMonth
        ) {
          const dayNum = d.getDate();
          if (dailyMap[dayNum] !== undefined) {
            dailyMap[dayNum]++;
          }
        }
      });

      return Object.entries(dailyMap).map(([day, count]) => ({
        name: `Day ${day}`,
        count,
      }));
    } else {
      // 12-Month overview
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthCounts = monthNames.map((name) => ({ name, count: 0 }));

      filtered.forEach((app) => {
        const dateVal = app.appliedAt || app.createdAt;
        if (!dateVal) return;
        const d = new Date(dateVal);
        if (!isNaN(d.getTime())) {
          const mIdx = d.getMonth();
          if (monthCounts[mIdx]) {
            monthCounts[mIdx].count += 1;
          }
        }
      });

      return monthCounts;
    }
  }, [stats.monthlyTrend, allApplications, trendRole, trendMonth]);

  // Rates
  const interviewToSelectedRate =
    stats.interview > 0
      ? ((stats.selected / stats.interview) * 100).toFixed(1)
      : 0;

  const rejectionRate =
    stats.totalApplicants > 0
      ? ((stats.rejected / stats.totalApplicants) * 100).toFixed(1)
      : 0;

  const getPeriodLabel = () => {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading && allApplications.length === 0) {
    return (
      <div style={{ backgroundColor: "white" }} className="emp-dash min-h-screen grid place-items-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
            Loading Recruitment Analytics...
          </p>
        </div>
      </div>
    );
  }

  if (error && allApplications.length === 0) {
    return (
      <div style={{ backgroundColor: "white" }} className="emp-dash min-h-screen p-6">
        <div className="emp-dash__card p-8 text-center max-w-md mx-auto">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-3">
            <FiX size={24} />
          </div>
          <h3 className="text-base font-bold text-gray-900">Dashboard Unavailable</h3>
          <p className="text-xs text-gray-500 mt-1">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider"
          >
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <main>
        {/* Header */}
        <div className="emp-dash__header">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="emp-dash__greeting text-lg sm:text-xl font-bold whitespace-nowrap">
              Recruitment <span>Dashboard</span>
            </h1>
            <span className="text-xs text-slate-400 font-medium hidden sm:inline">
              Track real-time applicant pipelines, hiring conversion, and score analytics.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="emp-dash__date-pill">
              <FaCalendarAlt />
              <span>{getPeriodLabel()}</span>
            </div>
            <button
              type="button"
              onClick={fetchDashboardData}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
            >
              <FiRefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>

        {/* 1. Top Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div
            className="emp-dash__stat cursor-pointer hover:scale-102 transition-transform"
            onClick={() => navigate("/job-applicants")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Applicants</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiUsers />
              </div>
            </div>
            <div className="emp-dash__stat-value text-indigo-700">
              <CountUp end={stats.totalApplicants || 0} duration={1.2} separator="," />
            </div>
            <div className="emp-dash__stat-meta">all applications received</div>
          </div>

          <div
            className="emp-dash__stat cursor-pointer hover:scale-102 transition-transform"
            onClick={() => navigate("/schedule-interviews")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Interviews</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rate">
                <FiUserPlus />
              </div>
            </div>
            <div className="emp-dash__stat-value text-amber-600">
              <CountUp end={stats.interview || 0} duration={1.2} separator="," />
            </div>
            <div className="emp-dash__stat-meta">interviews scheduled</div>
          </div>

          <div
            className="emp-dash__stat cursor-pointer hover:scale-102 transition-transform"
            onClick={() => navigate("/selected-candidates")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Selected Hires</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiUserCheck />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-700">
              <CountUp end={stats.selected || 0} duration={1.2} separator="," />
            </div>
            <div className="emp-dash__stat-meta">offers & selected hires</div>
          </div>

          <div
            className="emp-dash__stat cursor-pointer hover:scale-102 transition-transform"
            onClick={() => navigate("/rejected-candidates")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Rejected</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiUserX />
              </div>
            </div>
            <div className="emp-dash__stat-value text-rose-600">
              <CountUp end={stats.rejected || 0} duration={1.2} separator="," />
            </div>
            <div className="emp-dash__stat-meta">disqualified / rejected</div>
          </div>

          <div
            className="emp-dash__stat col-span-2 lg:col-span-1 cursor-pointer hover:scale-102 transition-transform"
            onClick={() => navigate("/job-positions-status")}
          >
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Vacancies</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiBriefcase />
              </div>
            </div>
            <div className="emp-dash__stat-value text-violet-700">
              <CountUp end={stats.totalVacancies || 0} duration={1.2} separator="," />
            </div>
            <div className="emp-dash__stat-meta">active job openings</div>
          </div>
        </div>

        {/* 2. Charts Section - Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Status Distribution - Donut Chart */}
          <div className="emp-dash__card p-6 flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Status Distribution
                </h3>
                <p className="text-[10px] text-gray-400">
                  Candidate breakdown across hiring stages
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-semibold outline-none focus:border-indigo-600 cursor-pointer"
                  value={statusRole}
                  onChange={(e) => setStatusRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  {stats.availableRoles?.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => navigate("/job-applicants")}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors hidden sm:inline cursor-pointer"
                >
                  View All →
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-[260px]">
              {stats.statusBreakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={stats.statusBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={105}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {stats.statusBreakdown.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                          stroke="#ffffff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomStatusTooltip />} />
                    <Legend
                      layout="horizontal"
                      align="center"
                      verticalAlign="bottom"
                      wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No status data available
                </div>
              )}
            </div>
          </div>

          {/* Score Distribution - Bar Chart */}
          <div className="emp-dash__card p-6 flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Technical Score Distribution
                </h3>
                <p className="text-[10px] text-gray-400">
                  Candidate assessment performance bands
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-semibold outline-none focus:border-indigo-600 cursor-pointer"
                  value={scoreRole}
                  onChange={(e) => setScoreRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  {stats.availableRoles?.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => navigate("/job-applicants")}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors hidden sm:inline cursor-pointer"
                >
                  Details →
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-[260px]">
              {stats.scoreDistribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={stats.scoreDistribution}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f1f5f9"
                    />
                    <XAxis
                      dataKey="range"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomScoreTooltip />} cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={26}>
                      {stats.scoreDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={SCORE_COLORS[entry.range] || "#4F46E5"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-gray-400">
                  No score data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3. Quality Metrics & Hiring TAT - Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Quality Metrics Cards */}
          <div className="emp-dash__card p-6 flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Candidate Quality Metrics
                </h3>
                <p className="text-[10px] text-gray-400">
                  Assessment excellence thresholds
                </p>
              </div>
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                <FiTrendingUp size={16} />
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-between">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <QualityCard
                  label="50%+ Score"
                  value={stats.qualityMetrics?.score50plus || 0}
                  total={stats.totalApplicants}
                  color="amber"
                />
                <QualityCard
                  label="60%+ Score"
                  value={stats.qualityMetrics?.score60plus || 0}
                  total={stats.totalApplicants}
                  color="emerald"
                />
                <QualityCard
                  label="70%+ Score"
                  value={stats.qualityMetrics?.score70plus || 0}
                  total={stats.totalApplicants}
                  color="cyan"
                />
                <QualityCard
                  label="80%+ Score"
                  value={stats.qualityMetrics?.score80plus || 0}
                  total={stats.totalApplicants}
                  color="indigo"
                />
                <QualityCard
                  label="90%+ Score"
                  value={stats.qualityMetrics?.score90plus || 0}
                  total={stats.totalApplicants}
                  color="purple"
                />
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">
                    Interview to Selection Rate
                  </span>
                  <span className="font-bold text-emerald-600">
                    {interviewToSelectedRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">
                    Overall Rejection Rate
                  </span>
                  <span className="font-bold text-rose-600">
                    {rejectionRate}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Hiring TAT (Turnaround Time) */}
          <div className="emp-dash__card p-6 flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Hiring Turnaround Time (TAT)
                </h3>
                <p className="text-[10px] text-gray-400">
                  Days taken from application to decision
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-semibold outline-none focus:border-indigo-600 cursor-pointer"
                  value={tatRole}
                  onChange={(e) => setTatRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  {stats.availableRoles?.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex-1 min-h-[260px]">
              {stats.tatDistribution?.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={stats.tatDistribution}
                    margin={{ top: 10, right: 10, left: -20, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f1f5f9"
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 10 }}
                      angle={-30}
                      textAnchor="end"
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      unit="d"
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        backgroundColor: "#0f172a",
                        color: "#fff",
                        fontSize: "11px",
                      }}
                      formatter={(val) => [`${val} Days`, "Avg TAT"]}
                    />
                    <Bar dataKey="days" radius={[6, 6, 0, 0]} barSize={26}>
                      {stats.tatDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-xs text-gray-400 gap-1">
                  <FiClock size={20} className="text-gray-300 mb-1" />
                  <span>Optimal TAT: 3 to 7 business days</span>
                  <span className="text-[10px] text-gray-400">
                    TAT records update automatically as candidate interviews proceed.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 4. Full-Width Monthly Application Trend (Hero Chart) */}
        <div className="emp-dash__card p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-gray-100 mb-4 gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <FiTrendingUp size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Monthly Application Trend
                  </h3>
                  <p className="text-[10px] text-gray-400">
                    Historical volume & candidate inflow trajectory
                  </p>
                </div>
              </div>
            </div>

            {/* Filter controls */}
            <div className="flex items-center gap-2 flex-wrap">
              <select
                className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold outline-none focus:border-indigo-600 cursor-pointer"
                value={trendRole}
                onChange={(e) => setTrendRole(e.target.value)}
              >
                <option value="All">All Roles</option>
                {stats.availableRoles?.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-1">
                <input
                  type="month"
                  className="bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold outline-none focus:border-indigo-600 cursor-pointer"
                  value={trendMonth}
                  onChange={(e) => setTrendMonth(e.target.value)}
                  title="Select month for daily application breakdown"
                />
                {trendMonth && (
                  <button
                    type="button"
                    onClick={() => setTrendMonth("")}
                    className="p-1.5 text-xs font-bold text-gray-400 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                    title="Clear month filter to see full year"
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => navigate("/job-applicants")}
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors hidden md:inline cursor-pointer pl-2"
              >
                View Pipeline →
              </button>
            </div>
          </div>

          <div className="min-h-[300px] w-full">
            {computedMonthlyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={computedMonthlyTrend}
                  margin={{ top: 15, right: 15, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorApplicationsGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTrendTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#4F46E5"
                    strokeWidth={3}
                    dot={{
                      r: 3.5,
                      fill: "#4F46E5",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 6,
                      fill: "#4F46E5",
                      stroke: "#ffffff",
                      strokeWidth: 2.5,
                    }}
                    fill="url(#colorApplicationsGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex flex-col items-center justify-center text-xs text-gray-400 gap-1">
                <FiTrendingUp size={24} className="text-gray-300 mb-1" />
                <span>No application trend records found for current filter.</span>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// Quality Card component for metrics
const QualityCard = ({ label, value, total, color }) => {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  const colorStyles = {
    amber: {
      bg: "bg-amber-50/70 border-amber-100",
      bar: "bg-amber-500",
      text: "text-amber-700",
    },
    emerald: {
      bg: "bg-emerald-50/70 border-emerald-100",
      bar: "bg-emerald-500",
      text: "text-emerald-700",
    },
    cyan: {
      bg: "bg-cyan-50/70 border-cyan-100",
      bar: "bg-cyan-500",
      text: "text-cyan-700",
    },
    indigo: {
      bg: "bg-indigo-50/70 border-indigo-100",
      bar: "bg-indigo-500",
      text: "text-indigo-700",
    },
    purple: {
      bg: "bg-purple-50/70 border-purple-100",
      bar: "bg-purple-500",
      text: "text-purple-700",
    },
  };

  const style = colorStyles[color] || colorStyles.indigo;

  return (
    <div className={`p-3 rounded-xl border ${style.bg} transition-all`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className={`text-base font-extrabold ${style.text}`}>
          <CountUp end={value} duration={1.2} />
        </span>
        <span className="text-[10px] font-bold text-gray-500">
          {percentage}%
        </span>
      </div>
      <div className="w-full h-1.5 bg-gray-200/60 rounded-full mt-2 overflow-hidden">
        <div
          className={`h-full rounded-full ${style.bar} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default RecruitmentDashboard;